import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Copy, Check, Crown, Coins, CheckCircle, PaperPlaneTilt, Warning, X } from "@phosphor-icons/react";

export default function LeagueDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [openMember, setOpenMember] = useState(null); // user_id whose modal is open
  // Gross vs Net leaderboard toggle. "Net" subtracts each member's WHS
  // handicap (via per-hole stroke index) from gross strokes — makes mixed-
  // ability leagues fair. Hidden when no member has set a handicap.
  const [mode, setMode] = useState("gross");

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/leagues/${id}`);
      setData(data);
    })();
  }, [id]);

  if (!data) return <div className="px-5 pt-6">Loading...</div>;
  const { league, leaderboard, progress, league_complete, winner, admin_payment, buy_ins, member_stats } = data;
  const ruleType = league.rule_type || "vs_par";
  const ruleLabel = { vs_par: "vs Par", total_strokes: "Total strokes", best_round: "Best round" }[ruleType];
  const courseReqs = league.course_requirements || [];
  const buyInsList = buy_ins || [];
  const havePaid = buyInsList.includes(user?.id);
  const isAdmin = user?.id === league.owner_id;
  const memberStatsById = Object.fromEntries((member_stats || []).map((s) => [s.user_id, s]));

  const currencySymbol = { GBP: "£", USD: "$", EUR: "€" }[league.pot_currency] || "";
  const inviteUrl = `${window.location.origin}/join/${league.code}`;
  const payoutMethodLabel = {
    paypal: "PayPal", monzo: "Monzo", revolut: "Revolut",
  }[league.payout_method] || null;

  const payoutLink = (method, handle, amount) => {
    if (!handle) return null;
    const h = encodeURIComponent(handle.replace(/^@/, ""));
    if (method === "paypal") return `https://paypal.me/${h}/${amount}`;
    if (method === "monzo") return `https://monzo.me/${h}/${amount}`;
    if (method === "revolut") return `https://revolut.me/${h}/${amount}`;
    return null;
  };

  const confirmPaid = async () => {
    await api.post(`/leagues/${id}/buyin`);
    const { data: fresh } = await api.get(`/leagues/${id}`);
    setData(fresh);
  };
  const clearPaid = async () => {
    await api.post(`/leagues/${id}/buyin/clear`);
    const { data: fresh } = await api.get(`/leagues/${id}`);
    setData(fresh);
  };

  const shareInvite = async () => {
    const shareData = {
      title: `Join my Fairway league: ${league.name}`,
      text: `Hop into "${league.name}" on Fairway. Code: ${league.code}`,
      url: inviteUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch { /* fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const top = leaderboard[0];
  const rest = leaderboard.slice(1);

  // Net leaderboard is a re-sort of the same array by net_score_to_par,
  // computed server-side. Only show the Net tab if at least one member has
  // a handicap on file — otherwise it'd just match the gross order.
  const anyHandicap = leaderboard.some((p) => typeof p.handicap === "number");
  const sortedNet = [...leaderboard].sort((a, b) => {
    const av = a.rounds_played > 0 ? (a.net_score_to_par ?? Infinity) : Infinity;
    const bv = b.rounds_played > 0 ? (b.net_score_to_par ?? Infinity) : Infinity;
    return av - bv;
  });
  const displayed = mode === "net" ? sortedNet : leaderboard;
  const displayedTop = displayed[0];
  const displayedRest = displayed.slice(1);

  return (
    <div className="px-5 pt-6 pb-8">
      <Link to="/app/leagues" className="btn-ghost -ml-2 mb-3" data-testid="league-back-link"><ArrowLeft size={20} weight="bold" /></Link>

      <p className="label-uppercase">League</p>
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)]" data-testid="league-name">{league.name}</h1>
      {league.description && <p className="text-[var(--text-secondary)] mb-1">{league.description}</p>}
      <div className="inline-flex items-center gap-2 mt-2 mb-4 bg-[var(--primary)] text-white px-3 py-1 rounded-full text-xs font-heading font-semibold uppercase tracking-wider" data-testid="league-rule-badge">
        Rule · {ruleLabel}
      </div>

      {/* Winner banner */}
      {league_complete && winner && (
        <div className="surface-card p-5 mb-4 bg-gradient-to-br from-[#C4543C] to-[#8a3a28] text-white relative overflow-hidden" data-testid="league-winner-banner">
          <Crown size={140} weight="fill" className="absolute -right-6 -bottom-6 opacity-15" />
          <p className="label-uppercase !text-white/80">League complete</p>
          <h2 className="font-heading font-black tracking-tighter-2 text-3xl mt-1">{winner.name} wins!</h2>
          {league.pot_amount != null && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white/15 backdrop-blur rounded-full px-4 py-2">
              <Coins size={18} weight="fill" />
              <span className="font-mono-num font-bold text-lg">{currencySymbol}{league.pot_amount}</span>
              <span className="text-sm opacity-90">to claim</span>
            </div>
          )}

          {/* Payout action */}
          {league.pot_amount != null && (
            <div className="mt-4">
              {isAdmin ? (
                payoutLink(winner.payment_method, winner.payment_handle, league.pot_amount) ? (
                  <a
                    href={payoutLink(winner.payment_method, winner.payment_handle, league.pot_amount)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="admin-pay-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-white text-[var(--accent-terracotta)] font-heading font-bold rounded-full px-5 py-3"
                  >
                    <PaperPlaneTilt size={18} weight="fill" />
                    Pay {winner.name} {currencySymbol}{league.pot_amount} via {winner.payment_method === "paypal" ? "PayPal" : winner.payment_method === "monzo" ? "Monzo" : "Revolut"}
                  </a>
                ) : (
                  <div className="rounded-2xl bg-white/15 backdrop-blur px-4 py-3 text-sm flex items-start gap-2" data-testid="admin-no-winner-handle">
                    <Warning size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
                    <span>{winner.name} hasn't added a payment handle yet. Ask them to set one in their Profile, then come back here to pay them out.</span>
                  </div>
                )
              ) : (
                <div className="rounded-2xl bg-white/15 backdrop-blur px-4 py-3 text-sm" data-testid="member-payout-info">
                  {payoutMethodLabel
                    ? <>The admin will pay the winner via <span className="font-bold">{payoutMethodLabel}</span>.</>
                    : <>The admin will sort the payout.</>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pot card */}
      {league.pot_amount != null && !league_complete && (
        <div className="surface-card-flat p-4 mb-4 bg-gradient-to-r from-[var(--accent-sand)] to-white" data-testid="league-pot-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-[var(--accent-terracotta)] text-white flex items-center justify-center">
                <Coins size={22} weight="fill" />
              </div>
              <div>
                <div className="label-uppercase">Money pot</div>
                <div className="font-mono-num font-black text-2xl text-[var(--primary)]" data-testid="league-pot-amount">
                  {currencySymbol}{league.pot_amount}
                </div>
              </div>
            </div>
            <div className="text-xs text-[var(--text-secondary)] text-right">
              Winner<br/>takes all
            </div>
          </div>
          {payoutMethodLabel && (
            <div className="mt-3 pt-3 border-t border-[var(--border-default)] text-xs text-[var(--text-secondary)] flex items-center gap-2">
              <PaperPlaneTilt size={14} weight="fill" className="text-[var(--accent-terracotta)]" />
              Buy-ins go to admin via <span className="font-bold text-[var(--primary)]">{payoutMethodLabel}</span>
              {" · "}<span className="font-mono-num font-bold text-[var(--primary)]">{buyInsList.length}/{data.members.length}</span> paid
            </div>
          )}
        </div>
      )}

      {/* Buy-in pay box — member-facing */}
      {league.pot_amount != null && !league_complete && !isAdmin && (
        <div className="surface-card-flat p-4 mb-4" data-testid="buyin-pay-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="label-uppercase">Your buy-in</p>
              <div className="font-mono-num font-black text-xl text-[var(--primary)]">{currencySymbol}{league.pot_amount}</div>
            </div>
            {havePaid && (
              <span className="inline-flex items-center gap-1 text-xs font-heading font-bold text-[var(--status-good)] bg-[var(--accent-turf)]/15 px-2.5 py-1 rounded-full" data-testid="havepaid-badge">
                <CheckCircle size={14} weight="fill" /> You've paid
              </span>
            )}
          </div>
          {admin_payment ? (
            <>
              <p className="text-xs text-[var(--text-secondary)] mb-3">
                Send <span className="font-mono-num font-bold text-[var(--primary)]">{currencySymbol}{league.pot_amount}</span> to <span className="font-bold text-[var(--primary)]">{admin_payment.admin_name}</span> via {payoutMethodLabel}.
              </p>
              {!havePaid ? (
                <div className="flex gap-2">
                  <a
                    href={payoutLink(admin_payment.method, admin_payment.handle, league.pot_amount)}
                    target="_blank" rel="noreferrer"
                    onClick={confirmPaid}
                    data-testid="buyin-pay-link"
                    className="btn-primary flex-1 !py-3 text-sm"
                  >
                    <PaperPlaneTilt size={16} weight="fill" /> Pay via {payoutMethodLabel}
                  </a>
                  <button onClick={confirmPaid} className="btn-secondary !py-3 text-sm" data-testid="buyin-mark-paid">
                    Already paid
                  </button>
                </div>
              ) : (
                <button onClick={clearPaid} className="btn-ghost text-xs underline" data-testid="buyin-unmark">
                  Mark as unpaid
                </button>
              )}
            </>
          ) : (
            <div className="text-xs text-[var(--text-secondary)] bg-[var(--accent-sand)] rounded-xl p-3 flex items-start gap-2">
              <Warning size={14} weight="fill" className="text-[var(--accent-terracotta)] flex-shrink-0 mt-0.5" />
              <span>Admin hasn't added a payment handle for {payoutMethodLabel || "their chosen app"} yet. Ask them to set it in Profile.</span>
            </div>
          )}
        </div>
      )}

      {/* Admin's view of who has paid */}
      {league.pot_amount != null && !league_complete && isAdmin && (
        <div className="surface-card-flat p-4 mb-4" data-testid="admin-buyin-tracker">
          <h3 className="font-heading font-bold text-[var(--primary)] mb-3">Buy-in tracker</h3>
          <div className="space-y-2">
            {data.members.map((m) => {
              const paid = buyInsList.includes(m.id);
              return (
                <div key={m.id} className="flex items-center justify-between" data-testid={`member-paid-${m.id}`}>
                  <span className="text-sm text-[var(--primary)] font-heading font-semibold">
                    {m.name}{m.id === league.owner_id && <span className="text-[10px] uppercase tracking-wider opacity-60 ml-1">(you)</span>}
                  </span>
                  {paid ? (
                    <span className="text-xs font-heading font-bold text-[var(--status-good)] flex items-center gap-1">
                      <CheckCircle size={14} weight="fill" /> Paid
                    </span>
                  ) : (
                    <span className="text-xs font-heading font-bold text-[var(--text-secondary)]">Pending</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-[var(--border-default)] flex items-center justify-between">
            <span className="label-uppercase">Collected</span>
            <span className="font-mono-num font-black text-[var(--primary)]" data-testid="admin-collected">
              {currencySymbol}{buyInsList.length * (league.pot_amount || 0)} of {currencySymbol}{data.members.length * (league.pot_amount || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Course requirements + per-player progress */}
      {courseReqs.length > 0 && (
        <div className="surface-card-flat p-4 mb-4" data-testid="course-requirements-block">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-[var(--primary)]">Course requirements</h3>
            {league_complete && (
              <span className="inline-flex items-center gap-1 text-xs font-heading font-bold text-[var(--status-good)]">
                <CheckCircle size={14} weight="fill" /> All done
              </span>
            )}
          </div>
          <div className="space-y-3">
            {progress && progress.map((p) => (
              <div key={p.user_id} className="rounded-xl border border-[var(--border-default)] p-3" data-testid={`progress-${p.user_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-semibold text-[var(--primary)]">{p.name}</span>
                  {p.complete && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-heading font-bold text-[var(--status-good)] uppercase">
                      <CheckCircle size={12} weight="fill" /> Done
                    </span>
                  )}
                </div>
                <div className="space-y-1.5">
                  {p.per_course.map((pc) => {
                    const pct = Math.min(100, Math.round((pc.rounds_played / pc.rounds_required) * 100));
                    return (
                      <div key={pc.course_id}>
                        <div className="flex justify-between text-[11px] text-[var(--text-secondary)] mb-1">
                          <span className="line-clamp-1 mr-2">{pc.course_name}</span>
                          <span className="font-mono-num font-bold">{pc.rounds_played}/{pc.rounds_required}</span>
                        </div>
                        <div className="h-2 bg-[var(--secondary)] rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="h-full"
                            style={{ background: pct >= 100 ? "var(--status-good)" : "var(--primary)" }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="surface-card-flat p-4 mb-5 bg-[var(--accent-sand)]" data-testid="invite-block">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-uppercase">Invite code</div>
            <div className="font-mono-num font-black text-2xl text-[var(--primary)] tracking-widest" data-testid="league-code">{league.code}</div>
          </div>
          <button onClick={shareInvite} className="btn-primary !py-3 !px-5" data-testid="share-invite-button">
            {copied ? <><Check size={18} weight="bold" /> Copied</> : <><Copy size={18} weight="bold" /> Share invite</>}
          </button>
        </div>
        <div className="mt-3 text-xs text-[var(--text-secondary)] break-all">
          <span className="label-uppercase block mb-1">One-tap link</span>
          <span className="font-mono-num" data-testid="league-invite-url">{inviteUrl}</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 gap-3">
        <h3 className="font-heading font-bold text-lg text-[var(--primary)]">Leaderboard</h3>
        {anyHandicap && (
          <div
            className="inline-flex p-0.5 rounded-full border border-[var(--border-default)] bg-[var(--bg-elevated)] text-xs font-heading font-bold"
            data-testid="leaderboard-mode-tabs"
          >
            <button
              type="button"
              onClick={() => setMode("gross")}
              data-testid="leaderboard-tab-gross"
              className={`px-3 py-1.5 rounded-full transition-colors ${mode === "gross" ? "bg-[var(--primary)] text-white" : "text-[var(--text-secondary)]"}`}
            >Gross</button>
            <button
              type="button"
              onClick={() => setMode("net")}
              data-testid="leaderboard-tab-net"
              className={`px-3 py-1.5 rounded-full transition-colors ${mode === "net" ? "bg-[var(--primary)] text-white" : "text-[var(--text-secondary)]"}`}
            >Net</button>
          </div>
        )}
      </div>

      {leaderboard.length === 0 ? (
        <div className="surface-card-flat p-5 text-[var(--text-secondary)]">No players yet.</div>
      ) : (
        <>
          {displayedTop && (
            <button
              onClick={() => setOpenMember(displayedTop.user_id)}
              data-testid="leaderboard-top"
              className="w-full text-left p-5 mb-3 rounded-3xl border bg-gradient-to-br from-[var(--primary)] to-[#0c2a20] text-white relative overflow-hidden"
              style={{ borderColor: "var(--border-default)" }}
            >
              <Crown size={120} weight="fill" className="absolute -right-6 -top-6 opacity-10" />
              <p className="label-uppercase !text-white/80">Leading{mode === "net" ? " · Net" : ""}</p>
              <div className="flex items-end justify-between mt-1">
                <div>
                  <div className="font-heading font-black text-3xl tracking-tighter-2" data-testid="leaderboard-top-name">{displayedTop.name}</div>
                  <div className="text-sm opacity-80">
                    {displayedTop.rounds_played} round{displayedTop.rounds_played === 1 ? "" : "s"} · Best {fmtDiff(mode === "net" ? displayedTop.net_best_round : displayedTop.best_round)}
                    {mode === "net" && typeof displayedTop.handicap === "number" && <span className="ml-2 opacity-70">· HCP {displayedTop.handicap}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="label-uppercase !text-white/80">{mode === "net" ? "Net vs Par" : ruleLabel}</div>
                  <div className="font-mono-num font-black text-4xl">{rulePrimary(displayedTop, mode === "net" ? "net" : ruleType)}</div>
                </div>
              </div>
              <MiniStats stats={memberStatsById[displayedTop.user_id]} dark />
            </button>
          )}

          <div className="space-y-2" data-testid="leaderboard-list">
            {displayedRest.map((p, i) => (
              <motion.button
                key={p.user_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setOpenMember(p.user_id)}
                className="w-full text-left surface-card-flat p-3"
                data-testid={`leaderboard-row-${p.user_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[var(--secondary)] text-[var(--primary)] flex items-center justify-center font-mono-num font-bold">{i + 2}</div>
                    <div>
                      <div className="font-heading font-bold text-[var(--primary)]">{p.name}</div>
                      <div className="text-xs text-[var(--text-secondary)]">
                        {p.rounds_played} round{p.rounds_played === 1 ? "" : "s"}
                        {mode === "net" && typeof p.handicap === "number" && <span className="ml-2">· HCP {p.handicap}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num font-bold text-lg text-[var(--primary)]">{p.rounds_played ? rulePrimary(p, mode === "net" ? "net" : ruleType) : "—"}</div>
                    <div className="text-xs text-[var(--text-secondary)]">Best {p.rounds_played ? fmtDiff(mode === "net" ? p.net_best_round : p.best_round) : "—"}</div>
                  </div>
                </div>
                <MiniStats stats={memberStatsById[p.user_id]} />
              </motion.button>
            ))}
          </div>
        </>
      )}

      {openMember && (
        <PlayerRoundsModal
          stats={memberStatsById[openMember]}
          ruleLabel={ruleLabel}
          onClose={() => setOpenMember(null)}
        />
      )}

      {/* Owner-only danger zone. Confirmation dialog first — a league
          with real member scores is a rare mis-click but a costly one
          if we didn't ask twice. Member rounds are preserved. */}
      {isAdmin && (
        <div className="mt-8 pt-4 border-t border-white/5" data-testid="league-danger-zone">
          <button
            type="button"
            onClick={async () => {
              const first = window.confirm(
                `Delete "${league.name}"?\n\n` +
                `Everyone's scores remain in their own history — only the league standings are removed. This can't be undone.`,
              );
              if (!first) return;
              try {
                await api.delete(`/leagues/${id}`);
                nav("/app/leagues");
              } catch (e) {
                window.alert(e?.response?.data?.detail || "Couldn't delete — please try again.");
              }
            }}
            data-testid="league-delete-btn"
            className="w-full text-xs font-bold py-2 opacity-70 hover:opacity-100 text-[var(--accent-terracotta)]"
          >
            Delete league
          </button>
          <p className="text-[10px] text-center italic text-[var(--text-secondary)] opacity-60">
            Only the owner sees this. Player rounds are kept — only the standings are cleared.
          </p>
        </div>
      )}
    </div>
  );
}

function fmtDiff(v) {
  if (v == null) return "—";
  if (v === 0) return "E";
  return v > 0 ? `+${v}` : String(v);
}

function rulePrimary(p, ruleType) {
  if (ruleType === "net") return fmtDiff(p.net_score_to_par);
  if (ruleType === "total_strokes") return p.total_strokes;
  if (ruleType === "best_round") return fmtDiff(p.best_round);
  return fmtDiff(p.score_to_par);
}

function MiniStats({ stats, dark }) {
  if (!stats) return null;
  const items = [
    { label: "HC", value: stats.handicap ?? "—" },
    { label: "Ace", value: stats.hole_in_ones },
    { label: "Eag", value: stats.eagles },
    { label: "Bir", value: stats.birdies },
    { label: "Par", value: stats.pars },
    { label: "Bog", value: stats.bogeys },
    { label: "Dbl+", value: stats.doubles_plus },
  ];
  return (
    <div className={`mt-3 pt-3 border-t ${dark ? "border-white/15" : "border-[var(--border-default)]"} grid grid-cols-7 gap-1 text-center`}>
      {items.map((it) => (
        <div key={it.label}>
          <div className={`font-mono-num font-bold ${dark ? "text-white" : "text-[var(--primary)]"} text-sm`}>{it.value}</div>
          <div className={`text-[9px] uppercase tracking-wider ${dark ? "text-white/60" : "text-[var(--text-secondary)]"}`}>{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function PlayerRoundsModal({ stats, ruleLabel, onClose }) {
  if (!stats) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="player-rounds-modal">
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[var(--border-default)] px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-xl text-[var(--primary)]" data-testid="player-modal-name">{stats.name}</h2>
            <p className="text-xs text-[var(--text-secondary)]">{stats.rounds.length} round{stats.rounds.length === 1 ? "" : "s"} in this league</p>
          </div>
          <button onClick={onClose} data-testid="player-modal-close" className="btn-ghost">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="surface-card-flat p-4">
            <p className="label-uppercase mb-2">Stats</p>
            <MiniStats stats={stats} />
          </div>

          <h3 className="font-heading font-bold text-[var(--primary)]">Rounds</h3>
          {stats.rounds.length === 0 ? (
            <div className="text-sm text-[var(--text-secondary)]">No rounds in this league yet.</div>
          ) : (
            <div className="space-y-2" data-testid="player-rounds-list">
              {stats.rounds.map((r) => (
                <div key={r.round_id} className="surface-card-flat p-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-semibold text-[var(--primary)] text-sm line-clamp-1">{r.course_name}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{new Date(r.started_at).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric"})}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num font-bold text-[var(--primary)]">{r.total_strokes}</div>
                    <div className="text-xs font-bold text-[var(--text-secondary)]">{fmtDiff(r.vs_par)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
